import React from 'react';
import './UserPage.css';

import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useParams } from 'react-router-dom';
import { loadUser, recordPayment } from './reducer';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from './selectors';
import Flex from '../../components/Flex/Flex';
import Text from '../../components/Text/Text';
import dayjs from 'dayjs';
import Table from '../../components/Table/Table';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import { toast } from 'react-toastify';

type UserPageProps = {
  embedded?: boolean;
};

const UserPage = React.memo(({ embedded = false }: UserPageProps) => {
  useIsAdmin();

  const dispatch = useDispatch();
  const { id } = useParams();

  const user = useSelector(selectUser);

  const [amount, setAmount] = React.useState('');
  const [commissionPercent, setCommissionPercent] = React.useState('10');
  const [referrerEmail, setReferrerEmail] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (id) {
      dispatch(loadUser(+id));
    }
  }, [dispatch, id]);

  React.useEffect(() => {
    setReferrerEmail(user?.user.referrerEmail ?? '');
  }, [user?.user.referrerEmail]);

  const handleRecordPayment = React.useCallback(() => {
    const parsedAmount = parseFloat(amount.replace(',', '.'));
    const parsedPercent = parseFloat(commissionPercent.replace(',', '.'));
    if (!id || !parsedAmount || parsedAmount <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }
    if (Number.isNaN(parsedPercent) || parsedPercent < 0 || parsedPercent > 100) {
      toast.error('Введите корректный процент комиссии');
      return;
    }
    dispatch(
      recordPayment({
        userId: +id,
        amount: parsedAmount,
        commissionPercent: parsedPercent,
        referrerEmail: referrerEmail.trim() || undefined,
        note: note.trim() || undefined,
      }),
    );
    setAmount('');
    setNote('');
  }, [dispatch, id, amount, commissionPercent, referrerEmail, note]);

  if (!user) {
    return null;
  }

  const statsTable = (
    <Table
      columns={[
        {
          title: 'Дата',
          dataIndex: 'date',
          render: (x) => <Text>{dayjs(x.date).format('YYYY-MM-DD')}</Text>,
        },
        {
          title: 'Поисков',
          dataIndex: 'searches',
        },
      ]}
      data={user.statistics || []}
      pagination={false}
    />
  );

  if (embedded) {
    return (
      <div className="v2-admin-user-detail">
        <section className="settings-panel v2-admin-user-card">
          <div className="settings-panel__head">
            <h2>{user.user.email}</h2>
          </div>
          {user.user.referrerEmail ? (
            <p className="v2-admin-user-meta">
              Реферер: <strong>{user.user.referrerEmail}</strong>
            </p>
          ) : (
            <p className="v2-admin-user-meta muted">Реферер не указан</p>
          )}
        </section>

        <section className="settings-panel v2-admin-user-card">
          <div className="settings-panel__head">
            <h2>Записать платёж</h2>
          </div>
          <p className="settings-field-hint">
            Комиссия начисляется только если у пользователя есть реферер (при регистрации или указан
            ниже).
          </p>
          <div className="v2-admin-payment-grid">
            <label className="settings-field">
              <span className="settings-field-label">Сумма, $</span>
              <Input placeholder="100" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">Комиссия, %</span>
              <Input
                placeholder="10"
                value={commissionPercent}
                onChange={(e) => setCommissionPercent(e.target.value)}
              />
            </label>
            <label className="settings-field">
              <span className="settings-field-label">Email реферера</span>
              <Input
                placeholder="referrer@example.com"
                value={referrerEmail}
                onChange={(e) => setReferrerEmail(e.target.value)}
              />
            </label>
            <label className="settings-field v2-admin-payment-grid__wide">
              <span className="settings-field-label">Примечание</span>
              <Input
                placeholder="Необязательно"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>
          <div className="settings-panel__footer">
            <button type="button" className="acid-button" onClick={handleRecordPayment}>
              Записать платёж
            </button>
          </div>
        </section>

        <section className="settings-panel v2-admin-user-card">
          <div className="settings-panel__head">
            <h2>Активность поиска</h2>
          </div>
          {(user.statistics?.length ?? 0) === 0 ? (
            <p className="table-hint">Нет данных по поискам.</p>
          ) : (
            <div className="table-scroll v2-admin-table-scroll">{statsTable}</div>
          )}
        </section>
      </div>
    );
  }

  return (
    <Flex className="UserPage" vertical>
      <Text size={4}>{user.user.email}</Text>

      <Flex vertical gap={12} className="UserPage__payment">
        <Text size={3} bold>
          Записать платёж
        </Text>
        <Text type="secondary" size={2}>
          Комиссия начисляется только если у пользователя есть реферер (при регистрации или указан
          ниже).
        </Text>
        {user.user.referrerEmail ? (
          <Text type="secondary" size={2}>
            Текущий реферер: {user.user.referrerEmail}
          </Text>
        ) : null}
        <Flex gap={12} wrap align="center">
          <Input
            placeholder="Сумма, $"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <Input
            placeholder="Комиссия, %"
            value={commissionPercent}
            onChange={(e) => setCommissionPercent(e.target.value)}
          />
          <Input
            placeholder="Email реферера"
            value={referrerEmail}
            onChange={(e) => setReferrerEmail(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <Input
            placeholder="Примечание (необязательно)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Button type="primary" onClick={handleRecordPayment}>
            Записать
          </Button>
        </Flex>
      </Flex>

      {statsTable}
    </Flex>
  );
});

export default UserPage;
